/**
 * Polls in the conversation (protocol polls.ts, 2026-10-29), as Discord
 * shows them: the poll card in a message (pick and Vote; your vote shows the
 * results, with Change and Remove; "N votes · 3h left"; the final results
 * with the winner once it ends), the "poll ended" notice, and the creator
 * (the composer's "+" menu: a dialog, a bottom sheet on a phone).
 */
import {
  POLL_ANSWER_MAX,
  POLL_ANSWERS_MAX,
  POLL_ANSWERS_MIN,
  POLL_DURATIONS_HOURS,
  POLL_QUESTION_MAX,
  Permissions,
  type Channel,
  type Message,
  type Poll,
  type Snowflake,
} from '@neosocial/protocol';
import { useEffect, useState, type CSSProperties } from 'react';
import { useT, type Translator } from '../i18n';
import { useFlag } from '../store/config';
import { useData, useDataShallow } from '../store/data';
import { pollCounts, pollOver, sendPoll, votePoll } from '../store/polls';
import { can, channelPermissions, userName } from '../store/selectors';
import { Btn, Icon, IconBtn, Select, Switch } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import { Emoji, quickReactions, recentEmoji } from './emoji';
import { reportError } from './social';

/** Whether you may post a poll here: the `polls` flag, Send Polls and Send Messages (as the server checks). */
export function useCanPoll(channel: Channel): boolean {
  const on = useFlag('polls');
  const perms = useDataShallow((d) => channelPermissions(d, channel));
  return on && can(perms, Permissions.SEND_POLLS) && can(perms, Permissions.SEND_MESSAGES);
}

/** Re-renders every `ms` while `on` (a poll's "3h left", and its end). */
function useNow(ms: number, on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const h = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(h);
  }, [ms, on]);
  return now;
}

/** "3d left", "5h left", "12m left". */
export function timeLeft(t: Translator['t'], ms: number): string {
  const min = Math.max(1, Math.ceil(ms / 60_000));
  if (min >= 1440) return t('pl.leftD', { n: Math.floor(min / 1440) });
  if (min >= 60) return t('pl.leftH', { n: Math.floor(min / 60) });
  return t('pl.leftM', { n: min });
}

/** Votes per answer as whole percents of all votes. */
function percents(p: Poll): { total: number; pct: Map<number, number>; top: number } {
  const counts = pollCounts(p);
  const total = counts.reduce((n, c) => n + c.count, 0);
  const pct = new Map(
    counts.map((c) => [c.id, total ? Math.round((c.count * 100) / total) : 0] as const),
  );
  return { total, pct, top: Math.max(0, ...counts.map((c) => c.count)) };
}

/** The poll card inside a message. */
export function PollView({ m, channel }: { m: Message; channel: Channel }) {
  const { t } = useT();
  const p = m.poll!;
  const pollsOn = useFlag('polls');
  const perms = useDataShallow((d) => channelPermissions(d, channel));
  // the server lets anyone who reads the history vote (as for a reaction)
  const canVote = pollsOn && (!channel.guild_id || can(perms, Permissions.READ_MESSAGE_HISTORY));
  const now = useNow(30_000, !p.results?.is_finalized);
  const over = pollOver(p, now);
  const counts = pollCounts(p);
  const mine = counts.filter((c) => c.me_voted).map((c) => c.id);
  const voted = mine.length > 0;
  const { total, pct, top } = percents(p);
  // auto: results once you voted; pick: choosing (a first vote or a change); results: peeking
  const [mode, setMode] = useState<'auto' | 'pick' | 'results'>('auto');
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const results = over || !canVote || mode === 'results' || (mode === 'auto' && voted);
  const multi = p.allow_multiselect;

  const submit = async (ids: number[]) => {
    setBusy(true);
    try {
      await votePoll(m, ids);
      setMode('auto');
      setPicked([]);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };
  const toggle = (id: number) =>
    setPicked((l) =>
      multi ? (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]) : l[0] === id ? [] : [id],
    );
  const q = p.question.text ?? '';
  return (
    <section
      className="pl"
      aria-label={t('pl.a11y', { q })}
      data-over={over || undefined}
      aria-busy={busy || undefined}
    >
      <header className="pl-h">
        <p className="pl-q" dir="auto">
          {q}
        </p>
        <small className="pl-sub">
          {over ? t('pl.ended') : t(multi ? 'pl.pickMany' : 'pl.pickOne')}
        </small>
      </header>
      <ul className="pl-list" role={results ? 'list' : multi ? 'group' : 'radiogroup'}>
        {p.answers.map((a, i) => {
          const c = counts[i]!;
          const emoji = a.poll_media.emoji?.name;
          const body = (
            <>
              {emoji ? <Emoji e={emoji} cls="pl-e" /> : null}
              <span className="pl-t" dir="auto">
                {a.poll_media.text}
              </span>
            </>
          );
          if (results) {
            const n = pct.get(a.answer_id) ?? 0;
            const win = over && top > 0 && c.count === top;
            return (
              <li
                key={a.answer_id}
                className="pl-a is-result"
                data-mine={c.me_voted || undefined}
                data-win={win || undefined}
                style={{ '--pl-pct': `${n}%` } as CSSProperties}
              >
                <span className="pl-bar" aria-hidden="true" />
                {body}
                {c.me_voted ? (
                  <span className="pl-badge" role="img" aria-label={t('pl.mine')}>
                    <Icon name="check-circle" size="s" />
                  </span>
                ) : win ? (
                  <span className="pl-badge" role="img" aria-label={t('pl.winner')}>
                    <Icon name="crown" size="s" />
                  </span>
                ) : null}
                <span className="pl-n nx-num">
                  {t('pl.votes', { n: c.count })} · {t('pl.pct', { n })}
                </span>
              </li>
            );
          }
          const on = picked.includes(a.answer_id);
          return (
            <li key={a.answer_id}>
              <button
                type="button"
                className="pl-a"
                role={multi ? 'checkbox' : 'radio'}
                aria-checked={on}
                disabled={busy}
                onClick={() => toggle(a.answer_id)}
              >
                {body}
                <span className={'pl-mark' + (multi ? ' sq' : '')} aria-hidden="true">
                  {on ? <Icon name="check" size="s" /> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <footer className="pl-f">
        <span className="pl-meta nx-num">
          {t('pl.votes', { n: total })} ·{' '}
          {over ? t('pl.closed') : p.expiry ? timeLeft(t, Date.parse(p.expiry) - now) : ''}
        </span>
        {!over && canVote ? (
          <span className="pl-acts">
            {results ? (
              voted ? (
                <>
                  <button
                    type="button"
                    className="ns-link"
                    disabled={busy}
                    onClick={() => void submit([])}
                  >
                    {t('pl.remove')}
                  </button>
                  <Btn
                    size="xs"
                    disabled={busy}
                    onClick={() => {
                      setPicked(mine);
                      setMode('pick');
                    }}
                  >
                    {t('pl.change')}
                  </Btn>
                </>
              ) : (
                <button type="button" className="ns-link" onClick={() => setMode('auto')}>
                  {t('pl.back')}
                </button>
              )
            ) : (
              <>
                <button type="button" className="ns-link" onClick={() => setMode('results')}>
                  {t('pl.show')}
                </button>
                <Btn
                  kind="primary"
                  size="xs"
                  busy={busy}
                  disabled={busy || !picked.length}
                  onClick={() => {
                    const same =
                      picked.length === mine.length && picked.every((x) => mine.includes(x));
                    if (same) setMode('auto');
                    else void submit(picked);
                  }}
                >
                  {t('pl.vote')}
                </Btn>
              </>
            )}
          </span>
        ) : null}
      </footer>
    </section>
  );
}

/** The winning line of a finished poll: "Pizza won with 3 votes.", a tie, or nobody. */
function winnerText(t: Translator['t'], p: Poll): string {
  const counts = pollCounts(p);
  const top = Math.max(0, ...counts.map((c) => c.count));
  if (top === 0) return t('pl.resultNone');
  const best = counts.filter((c) => c.count === top);
  if (best.length > 1) return t('pl.resultTie');
  const a = p.answers.find((x) => x.answer_id === best[0]!.id);
  const label = [a?.poll_media.emoji?.name, a?.poll_media.text].filter(Boolean).join(' ');
  return t('pl.resultWin', { a: label, n: t('pl.votes', { n: top }) });
}

/** The POLL_RESULT notice: "{name}'s poll {question} has closed." with the winner and View poll. */
export function PollResultNotice({
  m,
  guildId,
  onJump,
}: {
  m: Message;
  guildId?: Snowflake;
  onJump(id: Snowflake): void;
}) {
  const { t, tRich, fmt } = useT();
  const name = useData((d) => userName(d, m.author.id, guildId));
  const ref = m.referenced_message;
  const p = ref?.poll;
  return (
    <article className="msg sys pl-res" data-mid={m.id} id={`msg-${m.id}`} tabIndex={-1}>
      <div className="msg-gut">
        <span className="sys-ic">
          <Icon name="poll" size="s" />
        </span>
      </div>
      <div className="msg-body">
        <p className="sys-t">
          <span>
            {p
              ? tRich('pl.result', { name, q: p.question.text ?? '' })
              : tRich('pl.resultGone', { name })}
          </span>{' '}
          <time className="msg-time" dateTime={m.timestamp}>
            {fmt.stamp(m.timestamp)}
          </time>
        </p>
        {p ? (
          <p className="pl-res-win">
            <span dir="auto">{winnerText(t, p)}</span>{' '}
            <button type="button" className="ns-link" onClick={() => onJump(ref!.id)}>
              {t('pl.view')}
            </button>
          </p>
        ) : null}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------ the creator */

interface Draft {
  text: string;
  emoji: string | null;
}

/** The emoji an answer may take: your recent ones and the quick reactions, then a few more. */
function answerEmoji(): string[] {
  const more = ['👍', '👎', '❤️', '😂', '🔥', '🎉', '✅', '❌', '🍕', '☕', '🎮', '⚽', '🎵', '📚'];
  return [...new Set([...recentEmoji(), ...quickReactions(), ...more])].slice(0, 24);
}

/** Creates a poll (Discord's: a question, 2-10 answers with an emoji each, a duration, multiple answers). */
export function PollCreator({ channelId, onClose }: { channelId: Snowflake; onClose(): void }) {
  const { t, fmt } = useT();
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState<Draft[]>([
    { text: '', emoji: null },
    { text: '', emoji: null },
  ]);
  const [hours, setHours] = useState<number>(24);
  const [multi, setMulti] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emojiFor, setEmojiFor] = useState<number | null>(null);
  const len = (s: string) => [...s.trim()].length;
  const filled = answers.filter((a) => len(a.text) > 0);
  const valid =
    len(question) > 0 &&
    len(question) <= POLL_QUESTION_MAX &&
    filled.length >= POLL_ANSWERS_MIN &&
    answers.every((a) => len(a.text) <= POLL_ANSWER_MAX);
  const set = (i: number, patch: Partial<Draft>) =>
    setAnswers((l) => l.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const post = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await sendPoll(channelId, {
        question: { text: question.trim() },
        answers: filled.map((a) => ({
          poll_media: { text: a.text.trim(), ...(a.emoji ? { emoji: { name: a.emoji } } : {}) },
        })),
        duration: hours,
        allow_multiselect: multi,
        layout_type: 1,
      });
      onClose();
    } catch (e) {
      reportError(e);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('pl.new.title')}
      icon="poll"
      tone="mode"
      size="md"
      cls="pl-new"
      onClose={onClose}
      actions={
        <>
          <Btn size="sm" onClick={onClose}>
            {t('c.cancel')}
          </Btn>
          <Btn
            kind="primary"
            size="sm"
            busy={busy}
            disabled={!valid || busy}
            onClick={() => void post()}
          >
            {t('pl.new.post')}
          </Btn>
        </>
      }
    >
      <div className="pl-new-f">
        <label className="ns-field">
          <span className="ns-label">{t('pl.new.question')}</span>
          <input
            className="nx-input"
            dir="auto"
            value={question}
            maxLength={POLL_QUESTION_MAX * 2}
            placeholder={t('pl.new.questionPh')}
            aria-invalid={len(question) > POLL_QUESTION_MAX || undefined}
            onChange={(e) => setQuestion(e.target.value)}
            data-autofocus
          />
          <small className="pl-count nx-num">
            {fmt.num(len(question))} / {fmt.num(POLL_QUESTION_MAX)}
          </small>
        </label>
        <div className="ns-field">
          <span className="ns-label">{t('pl.new.answers')}</span>
          <ol className="pl-new-list">
            {answers.map((a, i) => (
              <li key={i}>
                <div className="pl-new-row">
                  <button
                    type="button"
                    className="pl-new-emo"
                    aria-label={t('pl.new.emoji', { n: i + 1 })}
                    aria-expanded={emojiFor === i}
                    onClick={() => setEmojiFor((x) => (x === i ? null : i))}
                  >
                    {a.emoji ? <Emoji e={a.emoji} /> : <Icon name="smile-plus" size="m" />}
                  </button>
                  <input
                    className="nx-input"
                    dir="auto"
                    value={a.text}
                    maxLength={POLL_ANSWER_MAX * 2}
                    placeholder={t('pl.new.answerPh')}
                    aria-label={t('pl.new.answerN', { n: i + 1 })}
                    aria-invalid={len(a.text) > POLL_ANSWER_MAX || undefined}
                    onChange={(e) => set(i, { text: e.target.value })}
                  />
                  {answers.length > POLL_ANSWERS_MIN ? (
                    <IconBtn
                      icon="trash"
                      label={t('pl.new.remove', { n: i + 1 })}
                      onClick={() => {
                        setAnswers((l) => l.filter((_, j) => j !== i));
                        setEmojiFor(null);
                      }}
                    />
                  ) : null}
                </div>
                {emojiFor === i ? (
                  <div
                    className="pl-new-emos"
                    role="group"
                    aria-label={t('pl.new.emoji', { n: i + 1 })}
                  >
                    {answerEmoji().map((e) => (
                      <button
                        key={e}
                        type="button"
                        aria-label={e}
                        aria-pressed={a.emoji === e}
                        onClick={() => {
                          set(i, { emoji: e });
                          setEmojiFor(null);
                        }}
                      >
                        <Emoji e={e} />
                      </button>
                    ))}
                    {a.emoji ? (
                      <button
                        type="button"
                        className="pl-new-noemo"
                        onClick={() => {
                          set(i, { emoji: null });
                          setEmojiFor(null);
                        }}
                      >
                        {t('pl.new.noEmoji')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
          {answers.length < POLL_ANSWERS_MAX ? (
            <Btn
              size="sm"
              icon="plus"
              className="pl-new-add"
              onClick={() => setAnswers((l) => [...l, { text: '', emoji: null }])}
            >
              {t('pl.new.add')}
            </Btn>
          ) : null}
        </div>
        <label className="ns-field">
          <span className="ns-label">{t('pl.new.duration')}</span>
          <Select
            className="nx-input ns-select"
            value={String(hours)}
            onChange={(e) => setHours(Number(e.target.value))}
          >
            {POLL_DURATIONS_HOURS.map((h) => (
              <option key={h} value={String(h)}>
                {t(`pl.dur.${h}`)}
              </option>
            ))}
          </Select>
        </label>
        <div className="pl-new-multi">
          <span>{t('pl.new.multi')}</span>
          <Switch checked={multi} onChange={setMulti} label={t('pl.new.multi')} />
        </div>
      </div>
    </Dialog>
  );
}
